// Order detail page at /orders/:id — shows full order, print button, void dialog.
import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiClient } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format/date";
import { useAuthStore } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { ArrowLeft, Percent, Printer, XCircle, Undo2 } from "lucide-react";
import { MoneyInput } from "@/components/ui/money-input";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { CollectDebtDialog } from "@/features/customers/components/collect-debt-dialog";
import { ReturnDialog } from "./return-dialog";

interface Serial { id: string; serialNo: string; }
interface OrderItem {
  id: string; variantId: string; quantity: number;
  unitPrice: string; discount: string; lineTotal: string;
  productName?: string | null; sku?: string | null;
  serialTracked?: boolean;
  serials?: Serial[];
}

// Phiếu hoàn hàng của đơn (khách trả lại hàng).
interface ReturnItemRow {
  id: string; orderItemId: string; quantity: number;
  unitPrice: string; lineTotal: string;
  productName?: string | null; sku?: string | null;
  serialNos?: string[];
}
interface OrderReturn {
  id: string; code: string; totalAmount: string;
  debtReduce: string; refundAmount: string; refundMethod: string;
  reason: string; createdByEmail?: string | null; createdAt: string;
  items?: ReturnItemRow[];
}
interface Order {
  id: string; code: string; status: string;
  subtotal: string; discountAmount: string; total: string;
  paidAmount: string; debtAmount?: string; paymentMethod?: string | null;
  cashierEmail?: string | null; note: string;
  customerId?: string | null; customerName?: string | null;
  createdAt: string; paidAt?: string | null;
  voidedAt?: string | null;
  items?: OrderItem[];
}

// "migrated" marks orders imported from the old nhanh.vn system.
const PAYMENT_LABEL: Record<string, string> = {
  cash: "Tiền mặt",
  qr: "QR / SePay",
  bank_manual: "CK tay",
  mixed: "Tiền mặt + CK",
  migrated: "Dữ liệu cũ",
  unknown: "Khác",
};

function fmtPayment(method?: string | null) {
  if (!method) return "—";
  return PAYMENT_LABEL[method] ?? method;
}

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

function fmtDate(s?: string | null) {
  if (!s) return "—";
  return formatDateTime(s);
}

export function OrderDetailPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [voidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState("");
  // Dieu chinh chiet khau don DA thanh toan (ke toan).
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [newDiscount, setNewDiscount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustErr, setAdjustErr] = useState<string | null>(null);
  // Thu nợ ngay tại đơn (yêu cầu kế toán 01/08).
  const [collectOpen, setCollectOpen] = useState(false);
  // Hoàn hàng — khách trả lại hàng (Sếp yêu cầu 01/08).
  const [returnOpen, setReturnOpen] = useState(false);
  // Sửa đơn giá 1 dòng của đơn đã thanh toán (kế toán ghi nhầm giá lúc bán).
  const [priceEdit, setPriceEdit] = useState<{ item: OrderItem; value: string; reason: string } | null>(null);
  const [priceErr, setPriceErr] = useState<string | null>(null);

  const { data: order, isLoading } = useQuery({
    queryKey: ["order", params.id],
    queryFn: () => apiGet<Order>(`/v1/orders/${params.id}`),
    enabled: !!params.id,
  });

  // Số dư công nợ hiện tại của khách — /v1/debts trả meta.balance.
  const customerId = order?.customerId ?? null;
  // apiGet bóc mất `meta` nên gọi thẳng apiClient để đọc meta.balance.
  const { data: debtMeta, refetch: refetchBalance } = useQuery({
    queryKey: ["customer-debt-balance", customerId],
    queryFn: async () => {
      const res = await apiClient.get<{ meta?: { balance?: string } }>("/v1/debts", {
        params: { customer_id: customerId, limit: 1 },
      });
      return res.data;
    },
    enabled: !!customerId,
  });

  // Các lần khách đã trả hàng của đơn này.
  const { data: returns = [] } = useQuery({
    queryKey: ["order-returns", params.id],
    queryFn: () => apiGet<OrderReturn[]>(`/v1/orders/${params.id}/returns`),
    enabled: !!params.id,
  });

  const printMutation = useMutation({
    mutationFn: () => {
      const wsId = localStorage.getItem("pos_workstation_id");
      const url = `/v1/orders/${params.id}/print${wsId ? `?workstation_id=${wsId}` : ""}`;
      return apiPost(url);
    },
  });

  const adjustMutation = useMutation({
    mutationFn: () =>
      apiPost(`/v1/orders/${params.id}/adjust-discount`, {
        discountAmount: newDiscount || "0",
        reason: adjustReason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", params.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setAdjustOpen(false);
      setAdjustErr(null);
    },
    onError: (e) => setAdjustErr(translateApiError(e)),
  });

  const adjustPriceMutation = useMutation({
    mutationFn: () =>
      apiPost(`/v1/orders/${params.id}/items/${priceEdit!.item.id}/adjust-price`, {
        unitPrice: priceEdit!.value || "0",
        reason: priceEdit!.reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", params.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      void refetchBalance();
      setPriceEdit(null);
      setPriceErr(null);
      toast.success("Đã sửa đơn giá");
    },
    onError: (e) => setPriceErr(translateApiError(e)),
  });

  const voidMutation = useMutation({
    mutationFn: () => apiPost(`/v1/orders/${params.id}/void`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", params.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setVoidOpen(false);
    },
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Đang tải...</div>;
  if (!order) return <div className="p-6 text-muted-foreground">Không tìm thấy đơn hàng.</div>;

  // Người có quyền quản lý phiếu (admin/quản lý/được cấp quyền) huỷ được đơn đã
  // thanh toán bất kể mốc 1 tiếng. Backend vẫn kiểm quyền + đảo tồn/serial.
  const canManageOrders =
    user?.role === "admin" || user?.role === "manager" || !!user?.canManageOrders;
  const canVoid = order.status === "paid" && canManageOrders;
  // Ke toan chinh chiet khau don da thanh toan — cung quyen sua gia nhu POS.
  const canEditPrice = user?.role === "admin" || !!user?.canEditPrice;
  const canAdjustDiscount =
    (order.status === "paid" || order.status === "partial_paid") && canEditPrice;

  function openPriceEdit(item: OrderItem) {
    setPriceEdit({
      item,
      value: String(Math.round(parseFloat(item.unitPrice) || 0)),
      reason: "",
    });
    setPriceErr(null);
  }

  function openAdjust() {
    setNewDiscount(String(Math.round(parseFloat(order!.discountAmount) || 0)));
    setAdjustReason("");
    setAdjustErr(null);
    setAdjustOpen(true);
  }

  // Xem truoc tong moi: coupon = tam tinh - chiet khau cu - tong cu (neu co).
  const subtotalNum = parseFloat(order.subtotal) || 0;
  const couponNum = Math.max(0, subtotalNum - (parseFloat(order.discountAmount) || 0) - (parseFloat(order.total) || 0));
  const previewTotal = Math.max(0, subtotalNum - (parseFloat(newDiscount) || 0) - couponNum);
  const paidNum = parseFloat(order.paidAmount) || 0;
  const previewDebt = Math.max(0, previewTotal - paidNum);

  // Nợ của RIÊNG đơn này (ghi nhận lúc bán) vs tổng nợ hiện tại của khách.
  const orderDebt =
    order.status === "voided"
      ? 0
      : parseFloat(order.debtAmount ?? "") || Math.max(0, (parseFloat(order.total) || 0) - paidNum);
  const customerBalance =
    debtMeta?.meta?.balance !== undefined ? parseFloat(debtMeta.meta.balance) || 0 : null;
  // Thu nợ = quyền thu ngân trở lên (BE: requireCashier); viewer chỉ xem.
  const canCollectDebt = !!user && user.role !== "viewer";

  // Hoàn hàng: đơn đã bán (paid/partial_paid) và còn mặt hàng chưa hoàn hết.
  const returnedQty: Record<string, number> = {};
  for (const rt of returns) {
    for (const ri of rt.items ?? []) {
      returnedQty[ri.orderItemId] = (returnedQty[ri.orderItemId] ?? 0) + ri.quantity;
    }
  }
  const orderItems = order.items ?? [];
  const hasReturnable = orderItems.some(
    (it) => it.quantity - (returnedQty[it.id] ?? 0) > 0,
  );
  const canReturn =
    (order.status === "paid" || order.status === "partial_paid") &&
    canCollectDebt &&
    hasReturnable;
  // Tỷ lệ tiền thực thu / giá trị hàng — để xem trước tiền hoàn cho đúng.
  const refundRatio = subtotalNum > 0 ? (parseFloat(order.total) || 0) / subtotalNum : 1;
  const returnedTotal = returns.reduce((s, rt) => s + (parseFloat(rt.totalAmount) || 0), 0);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/orders" })}>
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-xl font-semibold">{order.code}</h1>
        <span className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => printMutation.mutate()} disabled={printMutation.isPending}>
            <Printer size={16} className="mr-1" /> In lại
          </Button>
          {canAdjustDiscount && (
            <Button variant="outline" size="sm" onClick={openAdjust}>
              <Percent size={16} className="mr-1" /> Điều chỉnh CK
            </Button>
          )}
          {canReturn && (
            <Button variant="outline" size="sm" onClick={() => setReturnOpen(true)}>
              <Undo2 size={16} className="mr-1" /> Hoàn hàng
            </Button>
          )}
          {canVoid && (
            <Button variant="destructive" size="sm" onClick={() => setVoidOpen(true)}>
              <XCircle size={16} className="mr-1" /> Huỷ đơn
            </Button>
          )}
        </span>
      </div>

      {/* Header info */}
      <div className="grid grid-cols-2 gap-4 rounded-lg border bg-card p-4 text-sm">
        <div><span className="text-muted-foreground">Trạng thái: </span><strong>{order.status}</strong></div>
        <div><span className="text-muted-foreground">Thu ngân: </span>{order.cashierEmail ?? "—"}</div>
        <div><span className="text-muted-foreground">Thời gian: </span>{fmtDate(order.createdAt)}</div>
        <div><span className="text-muted-foreground">Thanh toán lúc: </span>{fmtDate(order.paidAt)}</div>
        <div><span className="text-muted-foreground">Phương thức: </span>{fmtPayment(order.paymentMethod)}</div>
        {order.note && <div><span className="text-muted-foreground">Ghi chú: </span>{order.note}</div>}
      </div>

      {/* Items */}
      <Table>
        <THead>
          <Tr><Th>Sản phẩm</Th><Th className="text-right">SL</Th><Th className="text-right">Đơn giá</Th><Th className="text-right">Thành tiền</Th></Tr>
        </THead>
        <TBody>
          {(order.items ?? []).map((item) => (
            <Tr key={item.id}>
              <Td>
                <div>{item.productName ?? item.variantId}</div>
                <div className="text-xs text-muted-foreground">{item.sku}</div>
                {item.serials?.map((s) => (
                  <div key={s.id} className="text-xs text-muted-foreground">SN: {s.serialNo}</div>
                ))}
              </Td>
              <Td className="text-right">{item.quantity}</Td>
              <Td className="text-right">
                {canAdjustDiscount ? (
                  <button
                    type="button"
                    onClick={() => openPriceEdit(item)}
                    className="text-primary hover:underline"
                    title="Sửa đơn giá dòng này"
                  >
                    {fmtVND(item.unitPrice)}
                  </button>
                ) : (
                  fmtVND(item.unitPrice)
                )}
              </Td>
              <Td className="text-right font-semibold">{fmtVND(item.lineTotal)}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {/* Totals */}
      <div className="rounded-lg border bg-card p-4 text-sm space-y-2">
        <div className="flex justify-between"><span className="text-muted-foreground">Tạm tính</span><span>{fmtVND(order.subtotal)}</span></div>
        {parseFloat(order.discountAmount) > 0 && (
          <div className="flex justify-between"><span className="text-muted-foreground">Giảm giá</span><span>-{fmtVND(order.discountAmount)}</span></div>
        )}
        <div className="flex justify-between border-t pt-2 text-base font-bold">
          <span>Tổng cộng</span><span className="text-primary">{fmtVND(order.total)}</span>
        </div>
        <div className="flex justify-between"><span className="text-muted-foreground">Tiền khách</span><span>{fmtVND(order.paidAmount)}</span></div>
        {orderDebt > 0 && (
          <div className="flex justify-between border-t pt-2">
            <span className="text-muted-foreground">Còn nợ đơn này</span>
            <span className="font-semibold text-amber-600 dark:text-amber-400">{fmtVND(String(orderDebt))}</span>
          </div>
        )}
      </div>

      {/* Thu nợ ngay tại đơn — kế toán không phải sang mục Công nợ (yêu cầu 01/08).
          Công nợ ghi theo KHÁCH (sổ cái chung), không phân bổ theo từng đơn. */}
      {order.customerId && orderDebt > 0 && (
        <div className="rounded-lg border bg-card p-4 text-sm space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-semibold">Công nợ khách: {order.customerName || "—"}</p>
              <p className="text-xs text-muted-foreground">
                Tổng nợ hiện tại của khách:{" "}
                <span className="font-semibold text-amber-600 dark:text-amber-400">
                  {customerBalance === null ? "…" : fmtVND(String(customerBalance))}
                </span>
              </p>
            </div>
            {canCollectDebt && (
              <Button size="sm" onClick={() => setCollectOpen(true)}>Thu nợ</Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Công nợ tính chung theo khách, không tách theo từng đơn — số "Còn nợ đơn này" là
            ghi nhận lúc bán, thu nợ xong sẽ không đổi. Xem lịch sử ở trang khách hàng.
          </p>
        </div>
      )}

      {/* Sửa đơn giá 1 dòng của đơn đã thanh toán */}
      {priceEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm space-y-4 rounded-xl border bg-card p-6 shadow-xl">
            <h2 className="text-lg font-semibold">Sửa đơn giá</h2>
            <p className="text-sm text-muted-foreground">
              {priceEdit.item.productName ?? "—"} — số lượng {priceEdit.item.quantity}.
              Tổng đơn, công nợ và chi tiêu của khách sẽ tự tính lại (có ghi sổ đối soát).
            </p>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Đơn giá mới (cho 1 cái)</span>
              <MoneyInput
                value={priceEdit.value}
                onChange={(v) => setPriceEdit((p) => (p ? { ...p, value: v } : p))}
                placeholder="0"
                className="w-full"
              />
            </label>
            <div className="rounded-md bg-muted/40 px-3 py-2 text-sm space-y-0.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Thành tiền dòng</span>
                <span className="font-semibold">
                  {fmtVND(
                    String(
                      Math.max(
                        0,
                        (parseFloat(priceEdit.value) || 0) * priceEdit.item.quantity -
                          (parseFloat(priceEdit.item.discount) || 0),
                      ),
                    ),
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tổng đơn mới</span>
                <span className="font-semibold">
                  {fmtVND(
                    String(
                      Math.max(
                        0,
                        (parseFloat(order.total) || 0) -
                          (parseFloat(priceEdit.item.lineTotal) || 0) +
                          Math.max(
                            0,
                            (parseFloat(priceEdit.value) || 0) * priceEdit.item.quantity -
                              (parseFloat(priceEdit.item.discount) || 0),
                          ),
                      ),
                    ),
                  )}
                </span>
              </div>
            </div>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Lý do (tùy chọn)</span>
              <Input
                value={priceEdit.reason}
                onChange={(e) => setPriceEdit((p) => (p ? { ...p, reason: e.target.value } : p))}
                placeholder="VD: ghi nhầm giá lúc bán"
              />
            </label>
            {priceErr && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{priceErr}</p>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setPriceEdit(null)}
                disabled={adjustPriceMutation.isPending}
              >
                Hủy
              </Button>
              <Button
                className="flex-1"
                onClick={() => adjustPriceMutation.mutate()}
                disabled={adjustPriceMutation.isPending}
              >
                {adjustPriceMutation.isPending ? "Đang lưu…" : "Lưu giá mới"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lịch sử khách trả hàng */}
      {returns.length > 0 && (
        <div className="rounded-lg border bg-card p-4 text-sm space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Khách đã trả lại hàng</h2>
            <span className="text-muted-foreground">
              Tổng giá trị hoàn:{" "}
              <span className="font-semibold text-amber-600 dark:text-amber-400">
                {fmtVND(String(returnedTotal))}
              </span>
            </span>
          </div>
          {returns.map((rt) => (
            <div key={rt.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs">{rt.code}</span>
                <span className="text-xs text-muted-foreground">
                  {fmtDate(rt.createdAt)}
                  {rt.createdByEmail ? ` · ${rt.createdByEmail}` : ""}
                </span>
              </div>
              <ul className="mt-1 space-y-0.5 text-xs">
                {(rt.items ?? []).map((ri) => (
                  <li key={ri.id}>
                    {ri.productName ?? "—"} × {ri.quantity}
                    {ri.serialNos && ri.serialNos.length > 0 && (
                      <span className="font-mono text-muted-foreground"> ({ri.serialNos.join(", ")})</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs">
                Giá trị {fmtVND(rt.totalAmount)}
                {parseFloat(rt.debtReduce) > 0 && ` · trừ công nợ ${fmtVND(rt.debtReduce)}`}
                {parseFloat(rt.refundAmount) > 0 &&
                  ` · trả khách ${fmtVND(rt.refundAmount)} (${
                    rt.refundMethod === "cash" ? "tiền mặt" : "chuyển khoản"
                  })`}
              </p>
              {rt.reason && <p className="mt-0.5 text-xs text-muted-foreground">Lý do: {rt.reason}</p>}
            </div>
          ))}
        </div>
      )}

      {returnOpen && (
        <ReturnDialog
          orderId={order.id}
          orderCode={order.code}
          items={orderItems}
          returnedQty={returnedQty}
          ratio={refundRatio}
          onClose={() => setReturnOpen(false)}
          onDone={() => {
            setReturnOpen(false);
            void refetchBalance();
            toast.success("Đã ghi nhận khách trả hàng");
          }}
        />
      )}

      {order.customerId && (
        <CollectDebtDialog
          open={collectOpen}
          customerId={order.customerId}
          customerName={order.customerName || "khách"}
          defaultAmount={orderDebt > 0 ? String(Math.round(orderDebt)) : ""}
          defaultNote={`Thu nợ đơn ${order.code}`}
          onClose={() => setCollectOpen(false)}
          onSuccess={() => {
            setCollectOpen(false);
            void refetchBalance();
            toast.success("Đã ghi nhận thu nợ");
          }}
        />
      )}

      {/* Adjust discount dialog */}
      {adjustOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold">Điều chỉnh chiết khấu</h2>
            <p className="text-sm text-muted-foreground">
              Đơn {order.code} — tạm tính {fmtVND(order.subtotal)}, khách đã trả {fmtVND(order.paidAmount)}.
              Tổng tiền, công nợ và chi tiêu của khách sẽ tự tính lại (có ghi sổ đối soát).
            </p>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Số tiền chiết khấu mới (cả đơn)</span>
              <MoneyInput value={newDiscount} onChange={setNewDiscount} placeholder="0" className="w-full" />
            </label>
            <div className="rounded-md bg-muted/40 px-3 py-2 text-sm space-y-0.5">
              <div className="flex justify-between"><span className="text-muted-foreground">Tổng mới</span><span className="font-semibold">{fmtVND(String(previewTotal))}</span></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{previewDebt > 0 ? "Khách sẽ nợ" : "Công nợ"}</span>
                <span className={previewDebt > 0 ? "font-semibold text-amber-600" : ""}>{fmtVND(String(previewDebt))}</span>
              </div>
            </div>
            <Input
              placeholder="Lý do điều chỉnh (nên ghi)..."
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
            />
            {adjustErr && <p className="text-sm text-destructive">{adjustErr}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAdjustOpen(false)} disabled={adjustMutation.isPending}>
                Đóng
              </Button>
              <Button size="sm" onClick={() => adjustMutation.mutate()} disabled={adjustMutation.isPending}>
                {adjustMutation.isPending ? "Đang lưu..." : "Xác nhận"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Void dialog */}
      {voidOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold text-destructive">Huỷ đơn hàng</h2>
            <p className="text-sm text-muted-foreground">Lý do huỷ đơn là bắt buộc. Tồn kho sẽ được hoàn về.</p>
            <Input
              placeholder="Nhập lý do..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setVoidOpen(false)} className="flex-1">Thoát</Button>
              <Button
                variant="destructive"
                onClick={() => voidMutation.mutate()}
                disabled={!reason.trim() || voidMutation.isPending}
                className="flex-1"
              >
                {voidMutation.isPending ? "Đang huỷ..." : "Xác nhận huỷ"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
